import { Timestamp } from "./compat";
import { StreamingServices } from "./creator";
import { SocialLinks } from "./event";

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export const TrackingEventTypes = ["visit", "click", "user", "card"] as const;
export type TrackingEventType = (typeof TrackingEventTypes)[number];

export const ClickTrackingContextTypes = [
  "donate",
  "merch",
  "tab",
  "register",
  "social",
  "streaming",
  "url",
] as const;
export type ClickTrackingContextType =
  (typeof ClickTrackingContextTypes)[number];

export interface ClickTrackingContext {
  type: ClickTrackingContextType;
  value: string;
}

export interface CardTrackingContext {
  label: string;
}

export interface UserTrackingContext {
  countryCode?: string;
  ipLocation?: Coordinates;
  ipaddress?: string;
  location?: Coordinates;
  userAgent?: string;
}

export interface BaseTrackingDoc<T> {
  uid: string; // user.uid,
  path: string; // location.pathname,
  createdAt: T; //serverTimestamp(),
  queryParams?: Record<string, string | null>;
}

export interface VisitTrackingEvent {
  event: Extract<TrackingEventType, "visit">;
  context?: Record<string, string>;
}

export interface ClickTrackingEvent {
  event: Extract<TrackingEventType, "click">;
  context: ClickTrackingContext;
}

export interface UserTrackingEvent {
  event: Extract<TrackingEventType, "user">;
  context: UserTrackingContext;
}

export interface CardTrackingEvent {
  event: Extract<TrackingEventType, "card">;
  context: CardTrackingContext;
}

export type TrackingEvent =
  | VisitTrackingEvent
  | ClickTrackingEvent
  | UserTrackingEvent
  | CardTrackingEvent;

export type TrackingDoc<T = Timestamp> = TrackingEvent & BaseTrackingDoc<T>;

export type TrackingFunction = (event: TrackingEvent) => Promise<void> | void;

export interface Vote<T = number> {
  label?: string;
  options?: Record<string, string>;
  votes?: Record<string, T>;
}

export type VoteDocument<T = number> = {
  vote?: Vote<T>;
} & { [id: string]: Vote<T> | undefined };

export interface EntriesDocument<T = number> {
  times?: { [key: number]: T | undefined };
  totals?: {
    all?: T;
    noLocation?: T;
    inVenue?: T;
  };
  zips?: { [key: string]: T | undefined };
}

export const EntryKeys = ["email", "phone"] as const;
export type EntryKey = (typeof EntryKeys)[number];

export type EntriesDocumentByKey<T = number> = Record<
  EntryKey,
  EntriesDocument<T> | undefined
>;

export interface EmailDocument {
  delivered?: number;
  opened?: number;
  clicked?: {
    [key in keyof SocialLinks | keyof StreamingServices]?: number;
  };
  openedBy?: {
    [key: string]: number;
  };
}

export interface UtmDocument {
  enabled: boolean;
  labels: Record<string, string>;
  utmSources?: Record<string, number>;
}

export interface ClickDocument {
  merch?: Record<string, number>;
  register?: {
    start: number;
  };
  social?: Record<string, number>;
  streaming?: Record<string, number>;
  tab?: Record<string, number>;
}
